/**
 * Table utility functions for sorting, formatting, and data handling
 */

/**
 * Sort an array of objects by a given field
 * @param {Array} data - Array of objects to sort
 * @param {string} field - Field name to sort by
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} Sorted array
 */
export function sortData(data, field, direction = 'desc') {
  if (!data || data.length === 0) return [];

  return [...data].sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];

    // Handle dates - check common date fields
    const dateFields = ['parcelCreatedAt', 'createdAt', 'date', 'updatedAt'];
    if (
      dateFields.includes(field) ||
      field.toLowerCase().includes('date') ||
      field.toLowerCase().includes('at')
    ) {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    // Handle numbers - check common numeric fields
    else if (
      field === 'price' ||
      field === 'amount' ||
      field === 'parcelPrice' ||
      typeof aVal === 'number'
    ) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    // Handle strings
    else if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase() || '';
      bVal = bVal?.toLowerCase() || '';
    }

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Format a date value for display in tables
 * @param {string|Date} dateValue - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatTableDate(dateValue, options = {}) {
  if (!dateValue) return '-';
  try {
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      ...options,
    });
  } catch {
    return '-';
  }
}

/**
 * Format currency for display
 * @param {number|string} value - Amount to format
 * @param {string} currency - Currency symbol (default ₹)
 * @returns {string} Formatted currency string
 */
export function formatTableCurrency(value, currency = '₹') {
  if (!value && value !== 0) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  return `${currency}${num.toFixed(0)}`;
}

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength = 30) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Extract display label from address object or string
 * @param {string|object} address - Address data
 * @returns {string} Display label
 */
export function getAddressLabel(address) {
  if (!address) return '-';
  if (typeof address === 'string') return address;
  return address.label || address.address || address.fullAddress || address.city || '-';
}
